import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Col,
  message,
  Row,
  Space,
  Table,
  notification,
  Tooltip,
  Switch,
  Modal,
  Form,
} from "antd";
import { FaSliders } from "react-icons/fa6";
import IMAGES from "../../../const/Image";
import bookingRequest from "../BookingRequest";
import bookingStatusRequest from "../BookingStatusRequest";
import bookingTypeRequest from "../BookingTypeRequest";
import { BookingFilters } from "./BookingFilter";

const initialFilter = { page: 1 };

export const Bookings = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [filterForm, setFilterForm] = useState({});
  const [filter, setFilter] = useState(initialFilter);
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [bookingStatusList, setBookingStatusList] = useState([]);
  const [bookingTypeList, setBookingTypeList] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const navigate = useNavigate();
  const showConfirm = Modal.confirm;

  // Fetch filtered bookings
  const fetchBookingFilterList = () => {
    setLoading(true);
    bookingRequest
      .bookingFilteredList(filter)
      .then((response) => {
        const rawData = response?.data?.data?.data || [];
        const totalCount = response?.data?.data?.totalItems || 0;
        console.log(rawData);
        setFilteredBookings(rawData);
        setTotalRecords(totalCount);
      })
      .catch((error) => {
        message.error("Failed to fetch Bookings.");
        console.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // Fetch booking status list
  const fetchBookingStatusList = () => {
    setLoading(true);
    bookingStatusRequest
      .bookingStatusList()
      .then((response) => {
        const rawData = response?.data?.data || [];
        setBookingStatusList(rawData);
      })
      .catch((error) => {
        message.error("Failed to fetch Booking Status List.");
        console.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // Fetch booking type list
  const fetchBookingTypeList = () => {
    setLoading(true);
    bookingTypeRequest
      .bookingTypeList()
      .then((response) => {
        const rawData = response?.data?.data || [];
        setBookingTypeList(rawData);
      })
      .catch((error) => {
        message.error("Failed to fetch Booking type List.");
        console.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchBookingFilterList();
  }, [filter]);

  useEffect(() => {
    fetchBookingStatusList();
  }, []);

  useEffect(() => {
    fetchBookingTypeList();
  }, []);

  // Handle filter changes
  const handleFilter = () => {
    setFilter({ ...filter, ...filterForm, page: 1 });
    setIsModalOpen(false);
  };

  // Handle pagination changes
  const handleChange = (pagination) => {
    setFilter({ ...filter, page: pagination.current });
  };

  // Reset filters
  const resetFilters = () => {
    form.resetFields();
    setFilterForm({});
    setFilter(initialFilter);
  };

  const showModal = () => {
    setIsModalOpen(true);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  const columns = (navigate) => [
    {
      title: "S No.",
      dataIndex: "sno",
      key: "sno",
      render: (_, item, index) => (filter.page - 1) * 10 + index + 1,
    },
    { title: "Trip Id", dataIndex: "tripId", key: "tripId" },
    {
      title: "Passenger Name",
      dataIndex: "passengerName",
      key: "passengerName",
    },
    { title: "From City", dataIndex: "fromCity", key: "fromCity" },
    {
      title: "To City",
      dataIndex: "toCity",
      key: "toCity",
    },
    { title: "Start Date", dataIndex: "startDate", key: "startDate" },
    { title: "End Date", dataIndex: "endDate", key: "endDate" },
    {
      title: "Duty Type",
      dataIndex: "bookingTypeName",
      key: "bookingTypeName",
    },
    {
      title: "Booking Status",
      dataIndex: "bookingStatusName",
      key: "bookingStatusName",
    },
    { title: "Booking Price", dataIndex: "bookingPrice", key: "bookingPrice" },
    {
      title: "Status",
      dataIndex: "isActive",
      width: 120,
      align: "center",
      render: (isActive, item) => (
        <Switch
          checked={isActive}
          checkedChildren="Active"
          unCheckedChildren="Inactive"
          onChange={(checked) => handleStatusChange(checked, item)}
        />
      ),
    },
    {
      title: "Action",
      dataIndex: "action",
      key: "action",
      render: (_, record) => (
        <Space size="middle">
          {record.isActive && (
            <Tooltip title="Edit Details">
              <span
                className="action-icon-bg"
                onClick={() =>
                  navigate(`/booking-management/booking/update/${record.id}`)
                }
              >
                <img src={IMAGES.EditIcon} alt="Edit" />
              </span>
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  const handleStatusChange = (checked, item) => {
    if (!item.id) {
      notification.error({
        message: "Booking Id is required to update status.",
      });
      return;
    }
    showConfirm({
      title: `Are you sure you want to ${
        checked ? "activate" : "deactivate"
      } this booking?`,
      okText: "Yes",
      cancelText: "No",
      onOk() {
        setLoading(true);
        bookingRequest
          .activeInactiveBooking({ isActive: checked, id: item.id })
          .then((res) => {
            if (res && res.status === 200) {
              notification.success({ message: "Booking updated successfully" });
              fetchBookingFilterList();
            } else {
              notification.error({ message: "Failed to update status" });
            }
          })
          .catch(() => {
            notification.error({ message: "Failed to update status" });
          })
          .finally(() => {
            setLoading(false);
          });
      },
    });
  };

  return (
    <>
      <div>
        <Row gutter={[8, 8]} justify="space-between" align="middle">
          <Col>
            <h1 className="app-heading-med">Booking List</h1>
          </Col>
          <Col>
            <div style={{ display: "flex", gap: "1rem" }}>
              <Button
                onClick={showModal}
                type="primary"
                className="addcity-btn"
              >
                <FaSliders />
              </Button>
              <Button
                onClick={resetFilters}
                type="primary"
                className="addcity-btn"
              >
                Reset
              </Button>
              <Button
                onClick={() => navigate("/booking-management/booking/add")}
                type="primary"
                className="addcity-btn"
              >
                <img src={IMAGES.AddIconFilters} alt="Add" />
              </Button>
            </div>
          </Col>
        </Row>
      </div>

      <Table
        size="middle"
        bordered
        rowClassName="row-class"
        columns={columns(navigate)}
        loading={loading}
        dataSource={filteredBookings}
        pagination={{
          total: totalRecords,
          current: filter.page,
          defaultCurrent: 1,
        }}
        onChange={handleChange}
      />

      <BookingFilters
        handleFilter={handleFilter}
        form={form}
        filterForm={filterForm}
        setFilterForm={setFilterForm}
        isModalOpen={isModalOpen}
        handleCancel={handleCancel}
        bookingStatusList={bookingStatusList}
        bookingTypeList={bookingTypeList}
      />
    </>
  );
};
